import { type Action, type History, type Location } from "history";
import { useCallback, useEffect, useRef } from "react";
import { type NavigateFunction, useNavigate } from "react-router-dom-v5-compat";

import { mobile } from "@btnet/utilities-webview";

import { routes } from "utilities/routes";
import { isDeepLinkEntry } from "utilities/routing/isDeepLinkEntry";

const isResultPath = (pathname: string) => pathname.includes(routes.search.resultModalPrefix);

/**
 * Follows the history entries a search-result modal has stacked above its parent page, so closing
 * can pop all of them. Opening a result pushes, so a replace on close leaves a duplicate of the
 * parent behind. Reading `history.action` at close time isn't enough: modals replace on save, which
 * hides a deep link, and some push sub-routes of their own (the photo "Replace" modal), which a
 * single `goBack()` leaves open.
 *
 * Entries are tracked by `location.key`, so browser Back/Forward inside the modal stay accurate.
 * Closing pops back to the parent when every entry was reached in-app, and replaces with the parent
 * URL when it can't: a deep-linked result has nothing behind it, and in a webview back is routed to
 * native `onGoBack()`, which pops the whole screen.
 */
export const trackSearchResultEntries = (history: History) => {
    // Keys of the entries from the parent page (index 0, unknown when it was the initial browser
    // entry) up to the current one. Null while no result modal is open.
    let keys: (string | undefined)[] | null = null;
    let index = 0;
    let canPop = false;
    let lastKey = history.location.key;

    const enter = (location: Location, parentKey: string | undefined, popsToParent: boolean) => {
        keys = [parentKey, location.key];
        index = 1;
        canPop = popsToParent;
    };

    if (isResultPath(history.location.pathname)) {
        enter(history.location, undefined, !isDeepLinkEntry(history, history.location));
    }

    const onChange = (location: Location, action: Action) => {
        const previousKey = lastKey;
        lastKey = location.key;

        if (!isResultPath(location.pathname)) {
            keys = null;
        } else if (keys === null) {
            enter(location, previousKey, action === "PUSH");
        } else if (action === "PUSH") {
            keys = [...keys.slice(0, index + 1), location.key];
            index += 1;
        } else if (action === "REPLACE") {
            keys[index] = location.key;
        } else {
            const found = location.key === undefined ? -1 : keys.indexOf(location.key);
            if (found >= 1) {
                index = found;
            } else {
                enter(location, undefined, false);
            }
        }
    };

    const stopListening = history.listen(onChange);

    // Tracking needs `history.listen`: it sees every entry synchronously, where a location effect
    // can batch two navigations into one render and miscount. Moving is left to `navigate`.
    const close = (parentUrl: string, navigate: NavigateFunction) => {
        if (keys !== null && canPop && !mobile.isWebview) {
            const steps = index;
            // A second close before the pop lands must not pop again.
            keys = null;
            navigate(-steps);
        } else {
            navigate(parentUrl, { replace: true });
        }
    };

    return { close, stopListening };
};

/** Returns a `beforeClose` that leaves the search-result modal in one step. */
export const useCloseSearchResult = (history: History) => {
    const navigate = useNavigate();
    // `navigate` changes identity with the location, so read it at close time rather than letting it
    // rebuild the tracker, which would forget the entries it has seen.
    const navigateRef = useRef(navigate);
    useEffect(() => {
        navigateRef.current = navigate;
    }, [navigate]);
    const tracker = useRef<ReturnType<typeof trackSearchResultEntries>>();

    useEffect(() => {
        const current = trackSearchResultEntries(history);
        tracker.current = current;
        return () => {
            current.stopListening();
            tracker.current = undefined;
        };
    }, [history]);

    return useCallback((parentUrl: string) => {
        if (tracker.current) {
            tracker.current.close(parentUrl, navigateRef.current);
        } else {
            navigateRef.current(parentUrl, { replace: true });
        }
    }, []);
};
